import React, { useState } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import PropControl from "../base/components/PropControl";
import { encURI, space } from '../base/utils/miscutils';
import { assert } from '../base/utils/assert';
import C from '../C';
import KStatus from '../base/data/KStatus';
import Advert from '../base/data/Advert';
import DataStore, { getDataPath } from '../base/plumbing/DataStore';
import { Card as CardCollapse } from '../base/components/CardAccordion';
import { fetchImpactBaseObjects } from '../base/data/ImpactPageData';
import Misc from '../base/components/Misc';
import SubCard from '../base/components/SubCard';
import SavePublishDeleteEtc from '../base/components/SavePublishDeleteEtc';
import DataItemBadge from '../base/components/DataItemBadge';


function ImpactSettingsControls({item, type, path, collapse}) {
	//assert(impactSettings);
	assert(item);
	assert(type);
	assert(path);

	const status = KStatus.DRAFT;

	const baseObjs = fetchImpactBaseObjects({id: item.id, type, status}).value;
	const {campaign, brand, ads, charities, impactDebits} = baseObjs || {};

	const impactSettingsPath = path.concat('impactSettings');
	// Get from data store so we know value is up to date
	const impactSettings = item.impactSettings;

	const branding = campaign?.branding || brand?.branding;

	const contents = <>
		{!baseObjs && <Misc.Loading/>}
		{impactDebits && <ImpactDebitControls impactSettings={impactSettings} path={impactSettingsPath} impactDebits={impactDebits} campaign={campaign} charities={charities} status={status}/>}
		{baseObjs && <BrandingControls branding={branding} impactSettings={impactSettings} impactSettingsPath={impactSettingsPath}/>}
		{ads && <AdControls ads={ads} impactSettings={impactSettings} impactSettingsPath={impactSettingsPath} status={status}/>}
	</>;

	return collapse ? (
		<CardCollapse className="impact-settings-controls" title="Impact Dashboard Settings" uncontrolled>
			{contents}
		</CardCollapse>
	) : (
		<SubCard className="impact-settings-controls">
			{contents}
		</SubCard>
	);
}


function ImpactDebitControls({ charities, impactDebits }) {
	// sort impact debits, ranking first by priority then by the cash value of the debit
	// "b - a" is used to invert the sort so the mapping below is in the right order
	impactDebits.sort((a, b) => {
		let result = (b.impact.priority || 0) - (a.impact.priority || 0);
		if (result === 0) result = (b.impact.amountGBP || 0) - (a.impact.amountGBP || 0);
		return result;
	});

	// for each debit, add its respective charity obj
	impactDebits.forEach((debit) => {
		debit.impact.charityObj = charities.find((charity) => charity.id === debit.impact.charity) || {};
	});

	return (
		<CardCollapse title="Impact Debits" uncontrolled className="debits">
			<ImpactDebitsEditor impactDebits={impactDebits} />
		</CardCollapse>
	);
}

export function VertiserImpactDebitEditor({vertiser}) {
	// fetch impact debits
	const baseObjs = fetchImpactBaseObjects({id: vertiser.id, type: C.TYPES.Advertiser, status: KStatus.DRAFT}).value;
	let { charities, impactDebits } = baseObjs || {};
	if (!baseObjs) return <h2>No impacts found</h2>;

	impactDebits.forEach((debit) => {
		debit.impact.charityObj = charities.find((charity) => charity.id === debit.impact.charity) || {};
	});

	return <ImpactDebitsEditor impactDebits={impactDebits} />;
}


function ImpactDebitsEditor({impactDebits}) {
	// sort priority or most £s first	
	impactDebits = impactDebits.sort((a, b) => {
		let result = (a.impact.priority || 0) - (b.impact.priority || 0);
		if (result === 0) result = (a.impact.amountGBP || 0) - (b.impact.amountGBP || 0);
		if (result === 0) result = a.id.localeCompare(b.id);
		return result;
	});
	// for each debit, check we actually have the info we need to display. If we don't, mark it as incomplete
	return impactDebits.map((debit, i) => <ImpactDebitEditor debit={debit} i={i} key={i} />);
}


function ImpactDebitEditor({debit, i}) {
	const path = DataStore.getDataPath({ id: debit.id, type: C.TYPES.ImpactDebit, status: KStatus.DRAFT});
	const storiesPath = [...path, 'storiesContent'];
	const ipath = [...path, 'impact'];

	let impact = debit.impact;

	// impact stories testimonals 
	// NB: using an array, and one bit will get picked below
	let visibleProps = [
		// props visible on first impact story
		<div>
			<div className="mb-3 p-3 bg-light card">
				<h3>Impact Cause</h3>
				<PropControl type="textarea" label="Impact Cause" prop="impactCause" path={storiesPath} help="In format 'Supporting {Impact cause}' For example, 'Supporting Food Redistribution'"/>
				<PropControl type="textarea" label="Impact Stat" prop="impactDesc" path={storiesPath} help="What good did the brand do to support the above cause? For example, 'Providing meals for children in need'"/>
			</div>
			<div className="mb-3 p-3 bg-light card">
				<h3>Bitesize fact</h3>
				<p>Replaced by image if empty</p>
				<PropControl type="textarea" label="Did-You-Know Fact" prop="fact" path={storiesPath}/>
				<PropControl type="textarea" label="Sources Name" prop="factSourceName" path={storiesPath}/>
				<PropControl type="textarea" label="Source URL" prop="factSourceURL" path={storiesPath} />
			</div>
		</div>,
		// props visible on second impact story
		<div>
			<div className="mb-3 p-3 bg-light card">
				<h3>Impact Cause</h3>
				<PropControl type="textarea" label="Impact Cause" prop="impactCause" path={storiesPath} help="In format 'Supporting {Impact cause}' For example, 'Supporting Food Redistribution'"/>
				<PropControl type="textarea" label="Impact Stat" prop="impactDesc" path={storiesPath} help="What good did the brand do to support the above cause? For example, 'Providing meals for children in need'"/>
			</div>
			<div className="mb-3 p-3 bg-light card">
				<h3>Testimonial</h3>
				<p>Defaults to description of charity if testimonial itself is left empty</p>
				<PropControl type="textarea" label="Testimonial Title" prop="testimonialHeader" path={storiesPath} help="Header of testimonial card"/>
				<PropControl type="textarea" label="Testimonial" prop="testimonialQuote" path={storiesPath} />
				<PropControl type="textarea" label="Testimonial Source Role" prop="testimonialJob" path={storiesPath} help="Role of whoever said the testimonial" />				
				<PropControl type="textarea" label="Testimonial Source" prop="testimonialPerson" path={storiesPath} help="Name of whoever said the testimonial"/>
			</div>
		</div>
	];

	const [show, setShow] = useState(false);
	const toggle = () => setShow(!show);
	return (
		<Row className={`impact-debit-row row-${i+1} justify-content-between mt-2`}>
			<p style={{width:"5%"}}>{i + 1}.</p>
			<DataItemBadge item={debit} href id={debit.id} status={KStatus.ALL_BAR_TRASH} type="ImpactDebit" style={{width:"30%"}}/>
			<PropControl type="number" prop="priority" path={path} label="priority"/>
			<PropControl type="number" prop="amountGBP" path={ipath} label="Amount in GBP" readOnly/>
			{/*i <= 1 && dataState */}
			<Modal size="xl" isOpen={show} toggle={toggle}>
				<ModalHeader toggle={toggle}>
					Props for Impact Debit {impact.name}
				</ModalHeader>
				<ModalBody>
					<PropControl type="number" prop="priority" path={path} label="Priority" className="font-weight-bold"/>
					<PropControl type="number" prop="amountGBP" path={ipath} label="Amount In GBP" readOnly/>
					<h2>Props for Impact Story {i+1}</h2>
					{visibleProps[i]}
					<br />
					<h2>Props required if card was in position {((i+1) % 2) + 1}</h2>
					<p>Only required if priorities are not set.</p>
					{visibleProps[(i+1)%2]}
				</ModalBody>
			</Modal>
			<Button className="btn btn-info" onClick={toggle}>Edit Impact Branding</Button>
			<SavePublishDeleteEtc position="relative" oneButton type={C.TYPES.ImpactDebit} id={debit.id} sendDiff />
		</Row>
	);
}


function BrandingControls({branding, impactSettingsPath}) {
	return <CardCollapse title="Customization" uncontrolled className="branding">
		<div className="d-flex flex-row justify-content-start">
			<PropControl path={impactSettingsPath} prop="logo" type="imgUpload" label="Override Logo" help="Override the logo used in the page." />
			{branding && <div className="d-flex flex-column">
				<img src={branding.logo} className="logo"/>
				<p className="ml-2 mt-1">Default logo</p>
			</div>}
		</div>
		<PropControl path={impactSettingsPath} prop="customCss" type="code" label="Custom CSS" />
	</CardCollapse>;
}


/**
 * 
 * @param {Object} p
 * @param {!Advert[]} p.ads
 * @returns 
 */
function AdControls({ads, impactSettings, impactSettingsPath, status}) {
	return <CardCollapse title="Adverts" uncontrolled className="ads">
		<PropControl path={impactSettingsPath} prop="showNonServedAds" type="checkbox" label="Show non-served ads (e.g. mocks)"/>
		<Row>
			{ads.map(ad => {
				const adPath = getDataPath({status, type: C.TYPES.Advert, id:ad.id});
				const hidden = Advert.isHiddenFromImpact(ad, impactSettings);
				const served = Advert.served(ad);

				return <Col xs={4} key={ad.id}>
					<SubCard className={space('m-2', hidden && 'hidden', (hidden === 'non-served') && 'faded')}>
						<div className="d-flex flex-row justify-content-between">
							<a href={`/#advert/${encURI(ad.id)}`} className="name">{ad.name || 'unnamed'}</a>
							<p className={hidden ? 'color-gl-light-red' : 'color-gl-blue'}>
								{hidden ? 'Hidden' : 'Shown'}
							</p>
						</div>
						<div><small>ID: {ad.id}</small></div>
						<PropControl type="checkbox" label="Hide from showcase?" prop="hideFromShowcase" path={adPath}
							help="Hides this ad from any showcasing. Applies to all pages that feature this ad, not just this page!"
						/>
						<p className={served ? 'color-gl-blue' : 'color-gl-light-red'}>
							{served ? 'Served' : 'Non-served'}
						</p>
						<SavePublishDeleteEtc oneButton type={C.TYPES.Advert} id={ad.id} targetPaths={['hideFromShowcase']}/>
					</SubCard>
				</Col>;
			})}
		</Row>
	</CardCollapse>;
}

export default ImpactSettingsControls;
